import * as z from 'zod';
import { crmIntegrationRefSchema, crmSegmentRefSchema } from '../crm-integrations';

// Import type enum
export const importTypeEnum = z.enum(['simple_list', 'csv', 'crm_integration']);
export type ImportType = z.infer<typeof importTypeEnum>;

// CSV Source - either base64 encoded content or download URL
export const csvSourceSchema = z.object({
  base64: z.string().optional(),
  downloadUrl: z.string().optional(),
});
export type CsvSource = z.infer<typeof csvSourceSchema>;

// CSV Column mapping value - can be a string (column name) or complex object
export const csvMappingValueSchema = z.union([
  z.string(),
  z.object({
    column: z.string(),
    default: z.string().optional(),
  }),
]);
export type CsvMappingValue = z.infer<typeof csvMappingValueSchema>;

// CSV Mapping - map from field path to column config
export const csvMappingSchema = z.record(z.string(), z.unknown());
export type CsvMapping = z.infer<typeof csvMappingSchema>;

// Simple list import request
export const simpleListImportRequestSchema = <T extends z.ZodTypeAny, C extends z.ZodTypeAny>(
  itemSchema: T,
  contextSchema: C
) =>
  z.object({
    type: z.literal('simple_list'),
    context: contextSchema,
    list: z.array(itemSchema),
  });

// CSV import request
export const csvImportRequestSchema = <C extends z.ZodTypeAny>(contextSchema: C) =>
  z.object({
    type: z.literal('csv'),
    context: contextSchema,
    csv: csvSourceSchema,
    mapping: csvMappingSchema,
  });

// CRM integration import request
export const crmIntegrationImportRequestSchema = <C extends z.ZodTypeAny>(contextSchema: C) =>
  z.object({
    type: z.literal('crm_integration'),
    context: contextSchema,
    crmIntegration: crmIntegrationRefSchema,
    crmSegment: crmSegmentRefSchema,
  });

// Generic import request type (for API calls)
export type SimpleListImportRequest<T, C> = {
  type: 'simple_list';
  context: C;
  list: T[];
};

export type CsvImportRequest<C> = {
  type: 'csv';
  context: C;
  csv: CsvSource;
  mapping: CsvMapping;
};

export type CrmIntegrationImportRequest<C> = {
  type: 'crm_integration';
  context: C;
  crmIntegration: { id: string };
  crmSegment: { id: string };
};

export type ImportRequest<T, C> =
  | SimpleListImportRequest<T, C>
  | CsvImportRequest<C>
  | CrmIntegrationImportRequest<C>;

// Analyze CSV request
export const analyzeCsvRequestSchema = <C extends z.ZodTypeAny>(contextSchema: C) =>
  z.object({
    csv: csvSourceSchema,
    context: contextSchema,
  });

export type AnalyzeCsvRequest<C> = {
  csv: CsvSource;
  context: C;
};

// Analyze CSV response
export const analyzeCsvResponseSchema = z.object({
  autoMapping: csvMappingSchema.nullable(),
  columns: z.array(z.unknown()).nullable(),
  stats: z.object({
    companiesCount: z.number().nullable(),
  }).nullable()
});
export type AnalyzeCsvResponse = z.infer<typeof analyzeCsvResponseSchema>;

// Import error detail
export const importErrorDetailSchema = z.object({
  index: z.number().nullable(),
  errorType: z.string().nullable(),
  errorMessage: z.string().nullable(),
  values: z.record(z.string(), z.unknown()).nullable(),
});
export type ImportErrorDetail = z.infer<typeof importErrorDetailSchema>;

// Import response. `audience` is optionally returned by the audience-import
// endpoint when the server creates (or upserts) the audience as part of the
// import, so the caller can poll it for readiness.
export const importResponseSchema = z.object({
  jobId: z.uuid().nullable(),
  successCount: z.number().nullable(),
  errorCount: z.number().nullable(),
  errorDetails: z.array(importErrorDetailSchema).nullable(),
  audience: z
    .object({
      id: z.uuid().nullable(),
      name: z.string().nullable(),
    })
    .nullable()
    .optional(),
});
export type ImportResponse = z.infer<typeof importResponseSchema>;
